export interface ShapePoint {
  x: number;
  y: number;
}

export interface CurvePoint extends ShapePoint {
  edgeIndex: number;
}

export interface EditableShapeGeometry {
  points: ShapePoint[];
  curvePoints: CurvePoint[];
}

interface PathSegment {
  start: ShapePoint;
  end: ShapePoint;
  curvePoint?: ShapePoint;
}

const COMMAND = /^[A-Za-z]$/;

const distance = (first: ShapePoint, second: ShapePoint): number =>
  Math.hypot(first.x - second.x, first.y - second.y);

const midpoint = (first: ShapePoint, second: ShapePoint): ShapePoint => ({
  x: (first.x + second.x) / 2,
  y: (first.y + second.y) / 2,
});

const cubicPoint = (
  start: ShapePoint,
  firstControl: ShapePoint,
  secondControl: ShapePoint,
  end: ShapePoint,
  t: number
): ShapePoint => {
  const inverse = 1 - t;

  return {
    x:
      inverse ** 3 * start.x +
      3 * inverse ** 2 * t * firstControl.x +
      3 * inverse * t ** 2 * secondControl.x +
      t ** 3 * end.x,
    y:
      inverse ** 3 * start.y +
      3 * inverse ** 2 * t * firstControl.y +
      3 * inverse * t ** 2 * secondControl.y +
      t ** 3 * end.y,
  };
};

const quadraticPoint = (
  start: ShapePoint,
  control: ShapePoint,
  end: ShapePoint,
  t: number
): ShapePoint => {
  const inverse = 1 - t;

  return {
    x: inverse ** 2 * start.x + 2 * inverse * t * control.x + t ** 2 * end.x,
    y: inverse ** 2 * start.y + 2 * inverse * t * control.y + t ** 2 * end.y,
  };
};

const arcMidpoint = (
  start: ShapePoint,
  end: ShapePoint,
  radiusX: number,
  radiusY: number,
  rotation: number,
  largeArc: boolean,
  sweep: boolean
): ShapePoint => {
  let rx = Math.abs(radiusX);
  let ry = Math.abs(radiusY);

  if (rx === 0 || ry === 0 || distance(start, end) === 0) {
    return midpoint(start, end);
  }

  const angle = (rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const halfDx = (start.x - end.x) / 2;
  const halfDy = (start.y - end.y) / 2;
  const transformedX = cos * halfDx + sin * halfDy;
  const transformedY = -sin * halfDx + cos * halfDy;
  const radiiScale =
    (transformedX * transformedX) / (rx * rx) +
    (transformedY * transformedY) / (ry * ry);

  if (radiiScale > 1) {
    const scale = Math.sqrt(radiiScale);

    rx *= scale;
    ry *= scale;
  }

  const numerator = Math.max(
    0,
    rx * rx * ry * ry -
      rx * rx * transformedY * transformedY -
      ry * ry * transformedX * transformedX
  );
  const denominator =
    rx * rx * transformedY * transformedY +
    ry * ry * transformedX * transformedX;
  const direction = largeArc === sweep ? -1 : 1;
  const factor =
    denominator === 0 ? 0 : direction * Math.sqrt(numerator / denominator);
  const centerX = factor * ((rx * transformedY) / ry);
  const centerY = factor * (-(ry * transformedX) / rx);
  const vectorAngle = (ux: number, uy: number, vx: number, vy: number) =>
    Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const startVector = {
    x: (transformedX - centerX) / rx,
    y: (transformedY - centerY) / ry,
  };
  const endVector = {
    x: (-transformedX - centerX) / rx,
    y: (-transformedY - centerY) / ry,
  };
  const startAngle = Math.atan2(startVector.y, startVector.x);
  let angleDelta = vectorAngle(
    startVector.x,
    startVector.y,
    endVector.x,
    endVector.y
  );

  if (!sweep && angleDelta > 0) angleDelta -= Math.PI * 2;

  if (sweep && angleDelta < 0) angleDelta += Math.PI * 2;

  const middleAngle = startAngle + angleDelta / 2;
  const absoluteCenter = {
    x: cos * centerX - sin * centerY + (start.x + end.x) / 2,
    y: sin * centerX + cos * centerY + (start.y + end.y) / 2,
  };

  return {
    x:
      absoluteCenter.x +
      cos * rx * Math.cos(middleAngle) -
      sin * ry * Math.sin(middleAngle),
    y:
      absoluteCenter.y +
      sin * rx * Math.cos(middleAngle) +
      cos * ry * Math.sin(middleAngle),
  };
};

export const getQuadraticControlPoint = (
  start: ShapePoint,
  curvePoint: ShapePoint,
  end: ShapePoint
): ShapePoint => ({
  x: 2 * curvePoint.x - (start.x + end.x) / 2,
  y: 2 * curvePoint.y - (start.y + end.y) / 2,
});

export const buildQuadraticShapePath = (
  points: ShapePoint[],
  curvePoints: CurvePoint[] = []
): string | undefined => {
  if (points.length < 3) return undefined;

  const curves = new Map(
    curvePoints.map((point) => [point.edgeIndex, point] as const)
  );
  const commands = [`M${points[0].x} ${points[0].y}`];

  points.forEach((point, edgeIndex) => {
    const end = points[(edgeIndex + 1) % points.length];
    const curve = curves.get(edgeIndex);

    if (curve) {
      const control = getQuadraticControlPoint(point, curve, end);

      commands.push(`Q${control.x} ${control.y} ${end.x} ${end.y}`);
    } else {
      commands.push(`L${end.x} ${end.y}`);
    }
  });

  commands.push("Z");
  return commands.join("");
};

export const getQuadraticShapeBounds = (
  points: ShapePoint[],
  curvePoints: CurvePoint[] = []
): { x: number; y: number; width: number; height: number } | undefined => {
  if (points.length === 0) return undefined;

  const curves = new Map(
    curvePoints.map((point) => [point.edgeIndex, point] as const)
  );
  const candidates = points.map((point) => ({ ...point }));

  points.forEach((start, edgeIndex) => {
    const curve = curves.get(edgeIndex);

    if (!curve) return;

    const end = points[(edgeIndex + 1) % points.length];
    const control = getQuadraticControlPoint(start, curve, end);

    (["x", "y"] as const).forEach((axis) => {
      const denominator = start[axis] - 2 * control[axis] + end[axis];

      if (denominator === 0) return;

      const t = (start[axis] - control[axis]) / denominator;

      if (t > 0 && t < 1) {
        candidates.push(quadraticPoint(start, control, end, t));
      }
    });
  });

  const xs = candidates.map((point) => point.x);
  const ys = candidates.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export const convertSvgPathToEditableGeometry = (
  path: string,
  minimumPointDistance: number
): EditableShapeGeometry | undefined => {
  const tokens =
    path.match(
      /[MLHVCSQTAZmlhvcsqtaz]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g
    ) ?? [];

  if (tokens.length === 0) return undefined;

  const segments: PathSegment[] = [];
  let current = { x: 0, y: 0 };
  let subpathStart: ShapePoint | undefined;
  let command = "";
  let index = 0;
  let previousCubicControl: ShapePoint | undefined;
  let previousQuadraticControl: ShapePoint | undefined;

  const hasNumbers = (count: number) =>
    index + count <= tokens.length && !COMMAND.test(tokens[index]);
  const number = () => Number(tokens[index++]);
  const point = (relative: boolean): ShapePoint => {
    const x = number();
    const y = number();

    return relative ? { x: current.x + x, y: current.y + y } : { x, y };
  };
  const addLine = (end: ShapePoint) => {
    segments.push({ start: { ...current }, end: { ...end } });
    current = end;
    previousCubicControl = undefined;
    previousQuadraticControl = undefined;
  };

  while (index < tokens.length) {
    if (COMMAND.test(tokens[index])) command = tokens[index++];

    if (!command) break;

    const relative = command === command.toLowerCase();

    switch (command.toUpperCase()) {
      case "M": {
        if (!hasNumbers(2)) break;

        current = point(relative);
        subpathStart = { ...current };
        command = relative ? "l" : "L";
        break;
      }
      case "L":
        if (hasNumbers(2)) addLine(point(relative));
        else command = "";

        break;
      case "H":
        if (hasNumbers(1)) {
          const value = number();

          addLine({ x: relative ? current.x + value : value, y: current.y });
        } else command = "";

        break;
      case "V":
        if (hasNumbers(1)) {
          const value = number();

          addLine({ x: current.x, y: relative ? current.y + value : value });
        } else command = "";

        break;
      case "C": {
        if (!hasNumbers(6)) {
          command = "";
          break;
        }

        const start = { ...current };
        const firstControl = point(relative);
        const secondControl = point(relative);
        const end = point(relative);

        segments.push({
          start,
          end,
          curvePoint: cubicPoint(start, firstControl, secondControl, end, 0.5),
        });
        current = end;
        previousCubicControl = secondControl;
        previousQuadraticControl = undefined;
        break;
      }
      case "S": {
        if (!hasNumbers(4)) {
          command = "";
          break;
        }

        const start = { ...current };
        const firstControl = previousCubicControl
          ? {
              x: 2 * current.x - previousCubicControl.x,
              y: 2 * current.y - previousCubicControl.y,
            }
          : { ...current };
        const secondControl = point(relative);
        const end = point(relative);

        segments.push({
          start,
          end,
          curvePoint: cubicPoint(start, firstControl, secondControl, end, 0.5),
        });
        current = end;
        previousCubicControl = secondControl;
        previousQuadraticControl = undefined;
        break;
      }
      case "Q": {
        if (!hasNumbers(4)) {
          command = "";
          break;
        }

        const start = { ...current };
        const control = point(relative);
        const end = point(relative);

        segments.push({
          start,
          end,
          curvePoint: quadraticPoint(start, control, end, 0.5),
        });
        current = end;
        previousQuadraticControl = control;
        previousCubicControl = undefined;
        break;
      }
      case "T": {
        if (!hasNumbers(2)) {
          command = "";
          break;
        }

        const start = { ...current };
        const control = previousQuadraticControl
          ? {
              x: 2 * current.x - previousQuadraticControl.x,
              y: 2 * current.y - previousQuadraticControl.y,
            }
          : { ...current };
        const end = point(relative);

        segments.push({
          start,
          end,
          curvePoint: quadraticPoint(start, control, end, 0.5),
        });
        current = end;
        previousQuadraticControl = control;
        previousCubicControl = undefined;
        break;
      }
      case "A":
        if (hasNumbers(7)) {
          const start = { ...current };
          const radiusX = number();
          const radiusY = number();
          const rotation = number();
          const largeArc = number() !== 0;
          const sweep = number() !== 0;
          const end = point(relative);

          segments.push({
            start,
            end,
            curvePoint: arcMidpoint(
              start,
              end,
              radiusX,
              radiusY,
              rotation,
              largeArc,
              sweep
            ),
          });
          current = end;
          previousCubicControl = undefined;
          previousQuadraticControl = undefined;
        } else command = "";

        break;
      case "Z":
        if (subpathStart && distance(current, subpathStart) > 0.0001) {
          addLine({ ...subpathStart });
        }

        command = "";
        break;
      default:
        command = "";
        break;
    }
  }

  if (segments.length < 3) return undefined;

  const points: ShapePoint[] = [{ ...segments[0].start }];
  const curvePoints: CurvePoint[] = [];

  segments.forEach((segment) => {
    const edgeIndex = points.length - 1;
    const closesShape = distance(segment.end, points[0]) < minimumPointDistance;

    if (closesShape) {
      if (segment.curvePoint && edgeIndex >= 0) {
        curvePoints.push({ edgeIndex, ...segment.curvePoint });
      }

      return;
    }

    if (distance(points[edgeIndex], segment.end) < minimumPointDistance) return;

    if (segment.curvePoint) {
      curvePoints.push({ edgeIndex, ...segment.curvePoint });
    }

    points.push({ ...segment.end });
  });

  if (points.length < 3) return undefined;

  return { points, curvePoints };
};

export const getEdgeMidpoint = (
  points: ShapePoint[],
  curvePoints: CurvePoint[],
  edgeIndex: number
): ShapePoint => {
  const curve = curvePoints.find((point) => point.edgeIndex === edgeIndex);

  return curve
    ? { x: curve.x, y: curve.y }
    : midpoint(points[edgeIndex], points[(edgeIndex + 1) % points.length]);
};
